import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

export default function AIChat({ user, onLogout }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChatHistory();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChatHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat/history?limit=50`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch chat history', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, { message: input });
      const aiMessage = { role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      toast.error('Failed to get response');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold" style={{ fontFamily: 'Fredoka', color: '#073B4C' }}>
            Chat with Mojo AI
          </h1>
          <p className="text-lg mt-2" style={{ color: '#073B4C', opacity: 0.7 }}>
            I'm here to listen. Tell me what's on your mind.
          </p>
        </div>

        <div className="glass-card p-6 mb-6" style={{ minHeight: '60vh', maxHeight: '60vh', overflowY: 'auto' }} data-testid="chat-container">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="text-6xl mb-4">💭</div>
              <p className="text-xl font-bold" style={{ fontFamily: 'Fredoka', color: '#073B4C' }}>
                Start a conversation
              </p>
              <p className="text-sm mt-2" style={{ opacity: 0.7 }}>Share your feelings, thoughts, or just say hi!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  data-testid={`chat-message-${msg.role}`}
                  className={`chat-message flex ${
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[70%] p-4 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-[#FFD166] text-[#073B4C]'
                        : 'bg-white border border-[#06D6A0]'
                    }`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className="text-xs mt-2" style={{ opacity: 0.5 }}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        <div className="glass-card p-4">
          <div className="flex gap-3">
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Type your message..."
              disabled={loading}
              className="rounded-xl border-2 border-transparent focus:border-[#FFD166] p-4 flex-1"
              rows={2}
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              data-testid="send-message-button"
              className="mojo-button"
              style={{ backgroundColor: '#FFD166', color: '#073B4C', minWidth: '60px' }}
            >
              {loading ? '...' : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      <Navigation active="chat" />
    </div>
  );
}